import { useMemo } from 'react';
import type { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { Product } from '@shared/components/ui/Product';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { ProductsWithoutStockModel } from '../models/ProductsWithoutStock';

// Hook: define y expone las columnas de AG Grid para ProductsWithoutStock
export const useAgGridColumns = ({ onOrderClick }: { onOrderClick: (product: ProductsWithoutStockModel) => void }) => {
  const { t } = useTranslation('productsWithoutStockModule');

  const allColumnDefs = useMemo<ColDef<ProductsWithoutStockModel>[]>(
    () => [
      {
        colId: 'product',
        field: 'product_name',
        headerName: t('table.product'),
        sortable: false,
        minWidth: 240,
        cellRenderer: ({ data }: { data: ProductsWithoutStockModel }) =>
          data ? (
            <Product
              id={data.product_id}
              name={data.product_name}
              code={data.product_code}
              src={data.product_image_url}
              enableLineClamp={true}
              showTooltip={true}
              showImageModal={true}
              data-testid={`product-${data.product_id}`}
            />
          ) : null,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'required',
        field: 'total_quantity',
        headerComponent: () => (
          <div className="flex items-center gap-2">
            <span className="text-title">{t('table.required')}</span>
            <InfoTooltip text={t('tooltips.required')} dataTestId="required-tooltip" position="bottom" />
          </div>
        ),
        headerTooltip: t('tooltips.required'),
        sortable: false,
        minWidth: 140,
        maxWidth: 160,
        cellRenderer: ({ data }: { data: ProductsWithoutStockModel }) => (
          <span className="text-sm font-medium">{data?.total_quantity ?? 0}</span>
        ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'available',
        field: 'available_stock',
        headerComponent: () => (
          <div className="flex items-center gap-2">
            <span className="text-title">{t('table.available')}</span>
            <InfoTooltip text={t('tooltips.available')} dataTestId="available-tooltip" position="bottom" />
          </div>
        ),
        headerTooltip: t('tooltips.available'),
        sortable: false,
        minWidth: 140,
        maxWidth: 160,
        cellRenderer: ({ data }: { data: ProductsWithoutStockModel }) => (
          <span className="text-sm font-medium">{data?.available_stock ?? 0}</span>
        ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'deficit',
        field: 'stock_deficit',
        headerComponent: () => (
          <div className="flex items-center gap-2">
            <span className="text-title">{t('table.deficit')}</span>
            <InfoTooltip text={t('tooltips.deficit')} dataTestId="deficit-tooltip" position="bottom" />
          </div>
        ),
        headerTooltip: t('tooltips.deficit'),
        sortable: false,
        minWidth: 140,
        maxWidth: 160,
        cellRenderer: ({ data }: { data: ProductsWithoutStockModel }) => (
          <span className="text-sm font-medium">{data?.stock_deficit ?? 0}</span>
        ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'order',
        // sin field; se calcula a partir de orders
        headerComponent: () => (
          <div className="flex items-center gap-2">
            <span className="text-title">{t('table.order')}</span>
            <InfoTooltip text={t('tooltips.order')} dataTestId="order-tooltip" position="bottom" />
          </div>
        ),
        headerTooltip: t('tooltips.order'),
        sortable: false,
        minWidth: 140,
        maxWidth: 160,
        cellRenderer: ({ data }: { data: ProductsWithoutStockModel }) => (
          <button
            type="button"
            className="inline-flex items-center justify-center cursor-pointer hover:bg-hover-press rounded-full px-3 py-0.5 transition-colors bg-card-border text-sm light:bg-default-text light:text-white"
            onClick={() => data && onOrderClick(data)}
            data-testid={data ? `order-click-${data.product_id}` : undefined}
          >
            {data?.orders?.length ?? 0}
          </button>
        ),
        suppressHeaderMenuButton: true,
      },
    ],
    [t, onOrderClick],
  );

  return { allColumnDefs };
};
